import { useRef, useState } from 'react';
import { Activity, Bus, Droplets, Factory, GraduationCap, HeartPulse, Radio, Zap } from 'lucide-react';
import { MapViewport, type MapMarker } from '../components/MapViewport';
import { infrastructureLayer } from '../map/data/demoLayers';
import { Donut, MiniLineChart, Panel, Pill, ProgressBar, Segmented } from '../components/ui';
import type { WorldViewportHandle } from '../map/WorldViewport';

const brazilInfrastructurePoints = [
  [-46.63, -23.55], [-43.17, -22.91], [-47.88, -15.79], [-38.50, -12.97],
  [-34.88, -8.05], [-38.54, -3.73], [-48.49, -1.46], [-51.23, -30.03],
  [-49.27, -25.43], [-48.55, -27.59], [-60.02, -3.12], [-67.81, -9.97],
  [-54.62, -20.47], [-44.31, -2.53], [-35.21, -5.79], [-40.34, -20.32],
  [-49.26, -16.69], [-56.10, -15.60], [-63.90, -8.76], [-69.87, -4.23],
  [-42.81, -5.09], [-37.07, -10.91], [-51.93, -14.24], [-57.65, -19.01],
  [-45.89, -23.18], [-41.26, -8.76], [-52.67, -26.23], [-39.31, -7.23],
  [-50.99, -29.17], [-44.05, -19.92], [-47.06, -22.91], [-40.50, -9.39],
] as const;

const markers: MapMarker[] = brazilInfrastructurePoints.map(([longitude, latitude], index) => ({
  id: `asset-${index}`,
  longitude,
  latitude,
  tone: (['green','green','cyan','yellow','green','orange'] as const)[index % 6],
  glyph: index % 3 === 0 ? '⚡' : index % 3 === 1 ? '▣' : '●',
  label: `Asset BR-${String(index + 1).padStart(3, '0')}`,
}));


const alerts = [
  { id: 'power', title: 'Power outage detected', location: 'São Paulo, SP', time: '10:18 UTC', tone: 'red' as const, longitude: -46.63, latitude: -23.55, glyph: '⚡' },
  { id: 'water', title: 'Water supply degraded', location: 'Campinas, SP', time: '09:47 UTC', tone: 'yellow' as const, longitude: -47.06, latitude: -22.91, glyph: '◉' },
  { id: 'transport', title: 'Transport disruption', location: 'Rio de Janeiro, RJ', time: '09:12 UTC', tone: 'yellow' as const, longitude: -43.17, latitude: -22.91, glyph: '▣' },
];

const sectors = [
  { label: 'All Sectors', icon: Activity },
  { label: 'Energy', icon: Zap },
  { label: 'Transport', icon: Bus },
  { label: 'Water', icon: Droplets },
  { label: 'Telecom', icon: Radio },
  { label: 'Health', icon: HeartPulse },
  { label: 'Education', icon: GraduationCap },
  { label: 'Industry', icon: Factory },
] as const;

export function InfrastructurePage() {
  const mapController = useRef<WorldViewportHandle | null>(null);
  const [sector, setSector] = useState('All Sectors');
  const [period, setPeriod] = useState('7D');
  const [selectedAlert, setSelectedAlert] = useState<string>();
  const sectorLabels = sectors.map((item) => item.label);
  return (
    <div className="infrastructure-layout">
      <div className="sector-tabs">
        {sectors.map(({ label, icon: Icon }) => <button type="button" key={label} className={sector === label ? 'is-active' : ''} onClick={() => setSector(label)}><Icon size={16} />{label}</button>)}
      </div>
      <MapViewport controllerRef={mapController} markers={[...markers.slice(0, sector === 'All Sectors' ? markers.length : 18), ...alerts.map((alert) => ({ id: `alert-${alert.id}`, longitude: alert.longitude, latitude: alert.latitude, tone: alert.tone, glyph: alert.glyph, label: alert.title, pulse: true }))]} layers={[infrastructureLayer]} initialCamera={{ longitude: -52, latitude: -14, height: 7_500_000, pitch: -90 }} className="infrastructure-map">
        <Panel className="map-legend-card" title={<span className="eyebrow">Operational status</span>}>
          <span><i className="severity-dot severity-dot--green" />Operational</span>
          <span><i className="severity-dot severity-dot--yellow" />Degraded</span>
          <span><i className="severity-dot severity-dot--red" />Outage</span>
          <span><i className="severity-dot severity-dot--muted" />Planned maintenance</span>
        </Panel>
      </MapViewport>
      <aside className="infrastructure-rail">
        <Panel title={<span className="eyebrow">System health & metrics</span>}>
          <div className="health-grid">
            <div className="health-grid__donut"><Donut value={87} label="Healthy" tone="green" size={88} /><span>Overall health</span></div>
            <div><small>Operational assets</small><strong>9,482</strong><span className="text-green">↑ 2.3%</span></div>
            <div><small>Degraded assets</small><strong className="text-yellow">312</strong><span className="text-yellow">↑ 5.1%</span></div>
            <div><small>Outage assets</small><strong className="text-red">148</strong><span className="text-red">↑ 12.4%</span></div>
          </div>
        </Panel>
        <Panel title={<span className="eyebrow">Asset status distribution</span>}>
          <div className="asset-distribution"><Donut value={86} label="Operational" tone="green" size={120} /><div><span><i className="legend-square legend-square--green" />Operational <strong>8,582</strong></span><span><i className="legend-square legend-square--yellow" />Degraded <strong>312</strong></span><span><i className="legend-square legend-square--red" />Outage <strong>148</strong></span><span><i className="legend-square legend-square--muted" />Maintenance <strong>900</strong></span></div></div>
        </Panel>
        <Panel title={<span className="eyebrow">Sector health over time</span>} action={<Segmented items={['1D','7D','30D','90D']} value={period} onChange={setPeriod} />}>
          <div className="multi-line-chart">
            <MiniLineChart values={[92,91,93,92,94,95,94]} tone="green" height={70} />
            <MiniLineChart values={[81,85,79,84,82,87,85]} tone="blue" height={70} />
            <MiniLineChart values={[68,72,70,75,76,77,78]} tone="cyan" height={70} />
            <MiniLineChart values={[88,86,89,90,89,91,90]} tone="purple" height={70} />
          </div>
          <div className="sector-score-grid">
            {sectorLabels.slice(1).map((label, index) => <div key={label}><span>{label}</span><strong>{[92,85,78,90,88,83,80][index]}%</strong><ProgressBar value={[92,85,78,90,88,83,80][index]} tone={index === 0 ? 'green' : index === 2 ? 'cyan' : 'blue'} /></div>)}
          </div>
        </Panel>
        <Panel title={<span className="eyebrow">Critical alerts</span>} action={<button type="button" className="text-button" onClick={() => setSelectedAlert(undefined)}>Clear selection</button>}>
          <div className="alert-list">
            {alerts.map((alert) => <button type="button" key={alert.id} className={selectedAlert === alert.id ? 'is-selected' : ''} onClick={() => { setSelectedAlert(alert.id); void mapController.current?.flyTo([alert.longitude, alert.latitude, 2_000_000], { durationMs: 700 }); }}><span className={`alert-icon alert-icon--${alert.tone === 'red' ? 'red' : 'yellow'}`}>{alert.glyph}</span><span><strong>{alert.title}</strong><small>{alert.location}</small></span><time>{alert.time}</time><Pill tone={alert.tone}>{alert.tone === 'red' ? 'Critical' : 'Warning'}</Pill></button>)}
          </div>
        </Panel>
      </aside>
    </div>
  );
}
